import { call, put, select, all, takeLatest } from 'redux-saga/effects';
import { chapterActionTypes as types } from 'src/redux/actions/chapterActions';
import { ref as dbRef, get, set, push, update, remove } from "firebase/database";
import { ref as stoRef, uploadBytes, getDownloadURL, listAll, deleteObject } from "firebase/storage";
import notification from 'src/utils/notification';
import { removeEmptyFields } from 'src/utils/helpers';
import adminSelectors from 'src/redux/selectors/adminSelectors';
import { database, storage } from 'src/firebase';

function* fetchChapters({ payload, callback }) {
  try {
    const { comicId } = payload;
    const chapterDbRef = dbRef(database, `chapters/${comicId}`);
    const data = yield get(chapterDbRef);
    if (!data.exists()) {
      throw new Error('No data!');
    }
    yield put({ type: types.FETCH_CHAPTERS_SUCCESS, data: data.val() });
    notification.close();
    callback?.();
  } catch (error) {
    yield put({ type: types.FETCH_CHAPTERS_FAILURE, error });
    console.error(error);
    notification.showError(error.message);
  }
}

function* fetchChapter({ payload, callback }) {
  try {
    const { comicId, chapterId } = payload;
    const chapterStoRef = stoRef(storage, `comics/${comicId}/${chapterId}`);
    const { items } = yield listAll(chapterStoRef);

    const itemArray = yield all(
      items.map(async (itemRef) => {
        const itemUrl = await getDownloadURL(itemRef);
        return { name: itemRef.name, url: itemUrl };
      })
    );

    yield put({ type: types.FETCH_CHAPTER_SUCCESS });
    notification.close();
    callback?.(itemArray);
  } catch (error) {
    yield put({ type: types.FETCH_CHAPTER_FAILURE, error });
    console.error(error);
    notification.showError(error.message);
  }
}

function* addChapter({ payload, callback }) {
  try {
    const admin = yield select(adminSelectors.admin);
    const { comicId, name, chapterContent } = payload;
    const rootDbRef = dbRef(database);
    const { key } = yield push(rootDbRef);

    // upload chapter
    yield all(
      chapterContent.map(item => {
        const chapterStoRef = stoRef(storage, `comics/${comicId}/${key}/${item.name}`);
        uploadBytes(chapterStoRef, item.originFileObj);
      })
    );

    // set chapter data to database
    const chapterDbRef = dbRef(database, `chapters/${comicId}/${key}`);
    const currentTime = Date.now(); // also use for index
    yield set(chapterDbRef, {
      comicId,
      id: key,
      name,
      index: currentTime,
      createdAt: currentTime,
      createdBy: admin.email,
      itemCount: chapterContent.length
    });

    // update comic data
    const comicsDbRef = dbRef(database, `comics/${comicId}`);
    const data = yield get(comicsDbRef);
    if (!data.exists()) {
      throw new Error('No data!');
    }
    yield update(comicsDbRef, { chapterCount: data.val().chapterCount + 1 });

    yield put({ type: types.ADD_CHAPTER_SUCCESS });
    notification.close();
    notification.showSuccess('Success', 3);
    callback?.();
  } catch (error) {
    yield put({ type: types.ADD_CHAPTER_FAILURE, error });
    console.error(error);
    notification.showError(error.message);
  }
}

function* editChapter({ payload, callback }) {
  try {
    const admin = yield select(adminSelectors.admin);
    const { comicId, name, chapterContent, chapterId, shouldUpdateChapterContent } = payload;

    const chapterStoRef = stoRef(storage, `comics/${comicId}/${chapterId}`);
    const { items } = yield listAll(chapterStoRef);

    if (shouldUpdateChapterContent) {
      // delete current files
      yield all(
        items.map(itemRef => {
          deleteObject(itemRef);
        })
      );

      // upload new files
      yield all(
        chapterContent.map(item => {
          const chapterStoRef = stoRef(storage, `comics/${comicId}/${chapterId}/${item.name}`);
          uploadBytes(chapterStoRef, item.originFileObj);
        })
      );
    }

    // update chapter data to database
    const chapterDbRef = dbRef(database, `chapters/${comicId}/${chapterId}`);
    const currentTime = Date.now(); // also use for index
    yield update(chapterDbRef, {
      name,
      updatedAt: currentTime,
      updatedBy: admin.email,
      ...(shouldUpdateChapterContent && { itemCount: chapterContent.length })
    });

    yield put({ type: types.EDIT_CHAPTER_SUCCESS });
    notification.close();
    notification.showSuccess('Success', 3);
    callback?.();
  } catch (error) {
    yield put({ type: types.EDIT_CHAPTER_FAILURE, error });
    console.error(error);
    notification.showError(error.message);
  }
}

function* deleteChapter({ payload, callback }) {
  try {
    const { comicId, chapterId } = payload;
    const chapterStoRef = stoRef(storage, `comics/${comicId}/${chapterId}`);
    const { items } = yield listAll(chapterStoRef);

    // delete files recursively
    yield all(
      items.map(itemRef => {
        deleteObject(itemRef);
      })
    );

    // delete chapter data in database
    const chapterDbRef = dbRef(database, `chapters/${comicId}/${chapterId}`);
    yield remove(chapterDbRef);

    // update comic data
    const comicsDbRef = dbRef(database, `comics/${comicId}`);
    const data = yield get(comicsDbRef);
    if (!data.exists()) {
      throw new Error('No data!');
    }
    yield update(comicsDbRef, { chapterCount: data.val().chapterCount - 1 });

    yield put({ type: types.DELETE_CHAPTER_SUCCESS });
    notification.close();
    notification.showSuccess('Success', 3);
    callback?.();
  } catch (error) {
    yield put({ type: types.DELETE_CHAPTER_FAILURE, error });
    console.error(error);
    notification.showError(error.message);
  }
}

export default function* chapterSaga() {
  yield all([
    takeLatest(types.FETCH_CHAPTERS_REQUEST, fetchChapters),
    takeLatest(types.FETCH_CHAPTER_REQUEST, fetchChapter),
    takeLatest(types.ADD_CHAPTER_REQUEST, addChapter),
    takeLatest(types.EDIT_CHAPTER_REQUEST, editChapter),
    takeLatest(types.DELETE_CHAPTER_REQUEST, deleteChapter),
  ])
}
